import { officeConstants } from "../actions/constants";

const initialState = {
    currentOfficeId: localStorage.getItem("ao_currentOfficeId") || null,
    offices: [],
    categories: [],
    productsByCategory: {},
    modelsData: {},
    loading: false,
    error: null,
    officeLoading: false,
    officeError: null,
    selectedModels: {},
    selectedColors: {},
    cart: [],
    modelLoadingProgress: {},
    modelLoadErrors: {},
};

const getOfficeId = (office) => String(office?._id || office?.id || "");

const normalizeCurrentOfficeId = (currentOfficeId, offices = []) => {
    const cleanCurrent = String(currentOfficeId || "");

    if (!cleanCurrent) return null;

    if (cleanCurrent === "all") {
        return offices.length ? "all" : null;
    }

    const exists = offices.some((office) => getOfficeId(office) === cleanCurrent);

    return exists ? cleanCurrent : null;
};

const persistCurrentOfficeId = (officeId) => {
    if (officeId) {
        localStorage.setItem("ao_currentOfficeId", officeId);
        return;
    }

    localStorage.removeItem("ao_currentOfficeId");
};

const mergeProductsById = (currentProducts = [], incomingProducts = []) => {
    const result = Array.isArray(currentProducts) ? [...currentProducts] : [];
    const indexById = new Map();

    result.forEach((product, index) => {
        const id = String(product?._id || product?.id || '');
        if (id) indexById.set(id, index);
    });

    (Array.isArray(incomingProducts) ? incomingProducts : []).forEach((product) => {
        const id = String(product?._id || product?.id || '');

        if (!id) {
            result.push(product);
            return;
        }

        if (indexById.has(id)) {
            const index = indexById.get(id);
            result[index] = {
                ...result[index],
                ...product,
            };
            return;
        }

        indexById.set(id, result.length);
        result.push(product);
    });

    return result;
};

const officeReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'CACHE_PRODUCT_MODEL_PARTS': {
            const productId = String(action.payload?.productId || '').trim();
            if (!productId || !state.modelsData?.[productId]) return state;

            return {
                ...state,
                modelsData: {
                    ...state.modelsData,
                    [productId]: {
                        ...state.modelsData[productId],
                        parts: Array.isArray(action.payload?.parts) ? action.payload.parts : [],
                        partsAnalysis: action.payload?.analysis || null,
                    },
                },
            };
        }

        case officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS: {
            const nextErrors = { ...state.modelLoadErrors };
            delete nextErrors[action.payload.productId];

            return {
                ...state,
                modelLoadingProgress: {
                    ...state.modelLoadingProgress,
                    [action.payload.productId]: action.payload,
                },
                modelLoadErrors: nextErrors,
            };
        }

        case officeConstants.CLEAR_PRODUCT_MODEL_MODEL_PROGRESS: {
            const next = { ...state.modelLoadingProgress };
            delete next[action.payload.productId];
            return {
                ...state,
                modelLoadingProgress: next,
            };
        }

        case officeConstants.GET_OFFICES_REQUEST:
            return {
                ...state,
                loading: true,
                officeLoading: true,
                error: null,
                officeError: null,
            };

        case officeConstants.CREATE_OFFICE_REQUEST:
        case officeConstants.UPDATE_OFFICE_REQUEST:
        case officeConstants.GET_OFFICE_CATEGORIES_REQUEST:
        case officeConstants.GET_PRODUCTS_BY_CATEGORY_REQUEST:
        case officeConstants.GET_PRODUCT_MODEL_DATA_REQUEST:
        case officeConstants.UPDATE_PRODUCT_PRICE_REQUEST:
            return {
                ...state,
                loading: true,
                officeLoading: true,
                error: null,
                officeError: null,
            };

        case officeConstants.CREATE_OFFICE_SUCCESS: {
            const offices = [...state.offices, action.payload.office];
            const currentOfficeId = getOfficeId(action.payload.office) || state.currentOfficeId;

            persistCurrentOfficeId(currentOfficeId);

            return {
                ...state,
                loading: false,
                officeLoading: false,
                offices,
                currentOfficeId,
            };
        }

        case officeConstants.GET_OFFICES_SUCCESS: {
            const offices = Array.isArray(action.payload.offices) ? action.payload.offices : [];
            const currentOfficeId = normalizeCurrentOfficeId(state.currentOfficeId, offices);

            persistCurrentOfficeId(currentOfficeId);

            return {
                ...state,
                loading: false,
                officeLoading: false,
                offices,
                currentOfficeId,
            };
        }

        case officeConstants.UPDATE_OFFICE_SUCCESS: {
            const offices = state.offices.map((office) =>
                office._id === action.payload.office._id ? action.payload.office : office
            );

            const currentOfficeId = normalizeCurrentOfficeId(state.currentOfficeId, offices);

            persistCurrentOfficeId(currentOfficeId);

            return {
                ...state,
                loading: false,
                officeLoading: false,
                offices,
                currentOfficeId,
            };
        }

        case officeConstants.GET_OFFICE_CATEGORIES_SUCCESS:
            return {
                ...state,
                loading: false,
                officeLoading: false,
                categories: action.payload.categories,
            };

        case officeConstants.GET_PRODUCTS_BY_CATEGORY_SUCCESS: {
            const categorySlug = action.payload.categorySlug;
            const currentProducts = state.productsByCategory[categorySlug] || [];
            const incomingProducts = action.payload.products || [];

            return {
                ...state,
                loading: false,
                officeLoading: false,
                productsByCategory: {
                    ...state.productsByCategory,
                    [categorySlug]: mergeProductsById(currentProducts, incomingProducts),
                },
            };
        }

        case officeConstants.GET_PRODUCT_MODEL_DATA_SUCCESS: {
            const nextErrors = { ...state.modelLoadErrors };
            delete nextErrors[action.payload.productId];

            return {
                ...state,
                loading: false,
                officeLoading: false,
                modelLoadErrors: nextErrors,
                modelsData: {
                    ...state.modelsData,
                    [action.payload.productId]: {
                        geometry: action.payload.geometry,
                        materials: action.payload.materials,
                        baseDimensions: action.payload.dimensions,
                        textures: action.payload.textures,
                        fileType: action.payload.fileType,
                        performance: action.payload.performance || null,
                        product: action.payload.product && typeof action.payload.product === 'object'
                            ? action.payload.product
                            : null,
                    },
                },
            };
        }

        case officeConstants.UPDATE_PRODUCT_PRICE_SUCCESS:
            return {
                ...state,
                loading: false,
                officeLoading: false,
                productsByCategory: Object.keys(state.productsByCategory).reduce((acc, categorySlug) => {
                    acc[categorySlug] = state.productsByCategory[categorySlug].map((product) =>
                        product._id === action.payload.product._id ? action.payload.product : product
                    );
                    return acc;
                }, {}),
            };

        case officeConstants.SET_SELECTED_MODELS:
            return {
                ...state,
                selectedModels: {
                    ...state.selectedModels,
                    ...action.payload,
                },
            };

        case officeConstants.SET_COLORS:
            return {
                ...state,
                selectedColors: {
                    ...state.selectedColors,
                    ...action.payload,
                },
            };

        case officeConstants.SET_CURRENT_OFFICE:
            persistCurrentOfficeId(action.payload || null);

            return {
                ...state,
                currentOfficeId: action.payload || null,
            };

        case officeConstants.GET_OFFICES_FAILURE:
            return {
                ...state,
                loading: false,
                officeLoading: false,
                error: action.payload.error,
                officeError: action.payload.error,
            };

        case officeConstants.GET_PRODUCT_MODEL_DATA_FAILURE: {
            const productId = action.payload?.productId;
            const errorPayload = {
                productId,
                error: action.payload?.error || 'Ошибка загрузки модели',
                message: action.payload?.message || action.payload?.error || 'Ошибка загрузки модели',
                status: action.payload?.status || null,
                code: action.payload?.code || null,
                blocked: !!action.payload?.blocked,
                createdAt: new Date().toISOString(),
            };

            return {
                ...state,
                loading: false,
                officeLoading: false,
                error: errorPayload.error,
                officeError: errorPayload.error,
                modelLoadErrors: productId
                    ? {
                        ...state.modelLoadErrors,
                        [productId]: errorPayload,
                    }
                    : state.modelLoadErrors,
            };
        }

        case officeConstants.CREATE_OFFICE_FAILURE:
        case officeConstants.UPDATE_OFFICE_FAILURE:
        case officeConstants.GET_OFFICE_CATEGORIES_FAILURE:
        case officeConstants.GET_PRODUCTS_BY_CATEGORY_FAILURE:
        case officeConstants.UPDATE_PRODUCT_PRICE_FAILURE:
            return {
                ...state,
                loading: false,
                officeLoading: false,
                error: action.payload.error,
                officeError: action.payload.error,
            };

        default:
            return state;
    }
};

export default officeReducer;
